/**
 * « Mot de passe oublié » v2 (route account/reset-password, ouverte par le lien du formulaire de
 * connexion) — carte de la maquette 27-connexion.
 * Logique = screens/Account/ResetPasswordForm.tsx : POST auth/resetPassword { email }, mêmes
 * messages de succès et d'erreur (INVALID_EMAIL, USER_NOT_FOUND, sinon message utilisateur « auth »).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import { AccountShell, Notice } from './components/Ui';
import { E_MAIL } from '../../lib/typo';
import { Icon } from './components/Icon';

export function ResetPassword() {
  const [email, setEmail] = useState('');
  const [errorPwd, setErrorPwd] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // = ResetPasswordForm.tsx handleSubmit()
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorPwd('');
    setConfirmation('');
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = (await HTTP.post('/auth/resetPassword', { email }).send()) as any;
      if (response?.status === 200) {
        setConfirmation(
          'Vous allez bientôt recevoir un email pour réinitialiser votre mot de passe. Pensez à consulter vos courriers indésirables.',
        );
      } else {
        throw new Error('Une erreur inattendue est survenue');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const errorCode = error?.ErrorCode || error?.response?.data?.ErrorCode;
      let errorMessage: string;
      switch (errorCode) {
        case 'INVALID_EMAIL':
          errorMessage = "L’adresse email saisie n’est pas valide. Vérifiez la saisie, puis réessayez.";
          break;
        case 'USER_NOT_FOUND':
          errorMessage = "Aucun compte n’est associé à cette adresse. Vérifiez la saisie, ou créez un compte.";
          break;
        default: {
          const backendMessage = error?.Message || error?.response?.data?.Message || error?.message;
          const { title, description } = resolveUserError('auth', backendMessage ? new Error(backendMessage) : undefined);
          errorMessage = description ? `${title}. ${description}` : title;
        }
      }
      setErrorPwd(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AccountShell className="mmv2-acct--signin">
      <div className="mmv2-acct-signin">
        <button type="button" className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm mmv2-acct-back" onClick={() => navigate(-1)}>
          <Icon name="left" size={14} />
          Retour
        </button>
        <h1 className="mmv2-acct-signin__title">Mot de passe oublié</h1>
        <p className="mmv2-acct-signin__lede">
          Saisissez l’adresse {E_MAIL} de votre compte&nbsp;: nous vous envoyons un lien pour choisir un nouveau mot de passe.
        </p>
        <form className="mmv2-card mmv2-card--pad mmv2-acct-auth mmv2-acct-auth--plain" onSubmit={handleSubmit}>
          <div className="mmv2-field mmv2-acct-field">
            <label className="mmv2-label" htmlFor="mmv2-acct-reset-email">
              E-mail
            </label>
            <input
              id="mmv2-acct-reset-email"
              className="mmv2-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="mmv2-btn mmv2-btn--primary mmv2-btn--lg mmv2-btn--block mmv2-acct-auth__submit"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? 'Envoi…' : 'Recevoir le lien'}
            {!isLoading ? <Icon name="right" /> : null}
          </button>
          {errorPwd ? (
            <div className="mmv2-acct-auth__msg">
              <Notice tone="error" role="alert">
                {errorPwd}
              </Notice>
            </div>
          ) : null}
          {confirmation ? (
            <div className="mmv2-acct-auth__msg">
              <Notice tone="success" role="status">
                {confirmation}
              </Notice>
            </div>
          ) : null}
        </form>
      </div>
    </AccountShell>
  );
}
